import { useMemo, useState, type ReactNode } from 'react';
import {
  RotateCw,
  Activity,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { useRmLogs } from '@/api/rmlogs';
import { useConfig } from '@/api/config';
import type { OperationLog, RuntimeLogView } from '@/api/types';
import { cn } from '@/lib/utils';

export default function ServerActivityPage() {
  const { data: config } = useConfig();
  const { data, isLoading, isFetching, error, refetch } = useRmLogs();

  const current = data?.currentRuntime ?? null;
  const previous = data?.previousRuntimes ?? [];
  const operations = useMemo(
    () => [...(data?.operationLogs ?? [])].reverse(),
    [data?.operationLogs],
  );

  const runtimeLogDisabled = config ? !config.runtimeLogEnabled : false;
  const operationLogDisabled = config ? !config.operationLogEnabled : false;

  // Defaults to open so first-load users see data without an extra click.
  // Persist within the page lifetime via component state — not localStorage,
  // because the page is rarely revisited and a fresh-open reset is fine.
  const [previousOpen, setPreviousOpen] = useState(true);
  const [operationsOpen, setOperationsOpen] = useState(true);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="text-2xl font-semibold">Server Activity</h1>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm border hover:bg-muted disabled:opacity-50 transition-colors"
        >
          <RotateCw className={cn('h-4 w-4', isFetching && 'animate-spin')} />
          Refresh
        </button>
      </div>

      {isLoading && (
        <div className="py-12 text-center text-muted-foreground text-sm">
          Loading server activity…
        </div>
      )}

      {error && (
        <div className="py-3 px-4 rounded-md border border-destructive/30 bg-destructive/5 text-destructive text-sm">
          Failed to load server activity:{' '}
          {error instanceof Error ? error.message : String(error)}
        </div>
      )}

      {!isLoading && !error && (
        <>
          <CurrentRuntimeCard
            current={current}
            disabled={runtimeLogDisabled}
          />
          <CollapsibleSection
            title="Previous Runtimes"
            headingId="previous-runtimes-heading"
            count={previous.length}
            open={previousOpen}
            onToggle={() => setPreviousOpen((o) => !o)}
          >
            <PreviousRuntimesTable
              previous={previous}
              disabled={runtimeLogDisabled}
            />
          </CollapsibleSection>
          <CollapsibleSection
            title="Operation Logs"
            headingId="operation-logs-heading"
            count={operations.length}
            meta="last 30 days"
            open={operationsOpen}
            onToggle={() => setOperationsOpen((o) => !o)}
          >
            <OperationLogsTable
              operations={operations}
              disabled={operationLogDisabled}
            />
          </CollapsibleSection>
        </>
      )}
    </div>
  );
}

// Shared column layout for the runtime tables. table-fixed plus identical
// percentage widths in both Current and Previous tables is what guarantees
// the Status column lines up vertically — without this each <table> would
// auto-size its own columns from its own content.
function RuntimeColgroup() {
  return (
    <colgroup>
      <col className="w-[34%]" />
      <col className="w-[34%]" />
      <col className="w-[14%]" />
      <col className="w-[18%]" />
    </colgroup>
  );
}

function RuntimeTableHead() {
  return (
    <thead className="bg-muted/40">
      <tr className="text-left">
        <th className="px-3 py-2.5 font-medium">Started</th>
        <th className="px-3 py-2.5 font-medium">Terminated</th>
        <th className="px-3 py-2.5 font-medium">Duration</th>
        <th className="px-3 py-2.5 font-medium">Status</th>
      </tr>
    </thead>
  );
}

interface CurrentRuntimeCardProps {
  current: RuntimeLogView | null;
  disabled: boolean;
}

function CurrentRuntimeCard({ current, disabled }: CurrentRuntimeCardProps) {
  return (
    <section aria-labelledby="current-runtime-heading" className="space-y-2">
      <h2 id="current-runtime-heading" className="text-sm font-medium">
        Current Runtime
      </h2>
      <div className="border rounded-lg overflow-hidden bg-card">
        {disabled && (
          <div className="p-4">
            <RuntimeDisabledNotice />
          </div>
        )}
        {!disabled && !current && (
          <div className="p-4 text-sm text-muted-foreground">
            No active runtime session.
          </div>
        )}
        {!disabled && current && (
          <table className="w-full text-sm table-fixed">
            <RuntimeColgroup />
            <RuntimeTableHead />
            <tbody>
              <tr>
                <td className="px-3 py-1.5 font-mono text-xs text-muted-foreground">
                  {formatLocal(current.startAt)}
                </td>
                <td className="px-3 py-1.5 font-mono text-xs text-muted-foreground">
                  {formatLocal(current.terminatedAt)}
                </td>
                <td className="px-3 py-1.5 font-mono">
                  {current.duration || '—'}
                </td>
                <td className="px-3 py-1.5">
                  <StatusBadge status={current.status} />
                </td>
              </tr>
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}

interface PreviousRuntimesTableProps {
  previous: RuntimeLogView[];
  disabled: boolean;
}

function PreviousRuntimesTable({
  previous,
  disabled,
}: PreviousRuntimesTableProps) {
  // Newest first.
  const rows = useMemo(() => [...previous].reverse(), [previous]);
  return (
    <div className="border rounded-lg overflow-hidden bg-card">
      {disabled && (
        <div className="p-4">
          <RuntimeDisabledNotice />
        </div>
      )}
      {!disabled && (
        <table className="w-full text-sm table-fixed">
          <RuntimeColgroup />
          <RuntimeTableHead />
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td
                  colSpan={4}
                  className="py-12 text-center text-muted-foreground text-sm"
                >
                  No previous runtime sessions.
                </td>
              </tr>
            )}
            {rows.map((r, i) => (
              <tr
                key={`${r.startAt}-${i}`}
                className="border-t hover:bg-muted/10 transition-colors"
              >
                <td className="px-3 py-1.5 font-mono text-xs text-muted-foreground">
                  {formatLocal(r.startAt)}
                </td>
                <td className="px-3 py-1.5 font-mono text-xs text-muted-foreground">
                  {formatLocal(r.terminatedAt)}
                </td>
                <td className="px-3 py-1.5 font-mono">{r.duration || '—'}</td>
                <td className="px-3 py-1.5">
                  <StatusBadge status={r.status} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

interface OperationLogsTableProps {
  operations: OperationLog[];
  disabled: boolean;
}

function OperationLogsTable({ operations, disabled }: OperationLogsTableProps) {
  return (
    <div className="border rounded-lg overflow-hidden bg-card">
      {disabled && (
        <div className="p-4">
          <OperationDisabledNotice />
        </div>
      )}
      {!disabled && (
        <table className="w-full text-sm">
          <thead className="bg-muted/40">
            <tr className="text-left">
              <th className="px-3 py-2.5 font-medium">Timestamp</th>
              <th className="px-3 py-2.5 font-medium">Operation</th>
              <th className="px-3 py-2.5 font-medium">Target</th>
              <th className="px-3 py-2.5 font-medium">Name</th>
              <th className="px-3 py-2.5 font-medium">Result</th>
              <th className="px-3 py-2.5 font-medium">Error</th>
            </tr>
          </thead>
          <tbody>
            {operations.length === 0 && (
              <tr>
                <td
                  colSpan={6}
                  className="py-12 text-center text-muted-foreground text-sm"
                >
                  No operation logs in the last 30 days.
                </td>
              </tr>
            )}
            {operations.map((op) => (
              <tr
                key={op.id}
                className="border-t hover:bg-muted/10 transition-colors"
              >
                <td className="px-3 py-1.5 font-mono text-xs text-muted-foreground">
                  {formatLocal(op.timestamp)}
                </td>
                <td className="px-3 py-1.5">{op.operation}</td>
                <td className="px-3 py-1.5 text-muted-foreground">
                  {op.targetType}
                </td>
                <td className="px-3 py-1.5 font-mono text-xs">
                  {op.targetName || '—'}
                </td>
                <td className="px-3 py-1.5">
                  <ResultBadge result={op.result} />
                </td>
                <td className="px-3 py-1.5 text-destructive text-xs">
                  {op.errorMessage}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

interface CollapsibleSectionProps {
  title: string;
  headingId: string;
  count?: number;
  meta?: string;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}

function CollapsibleSection({
  title,
  headingId,
  count,
  meta,
  open,
  onToggle,
  children,
}: CollapsibleSectionProps) {
  const bodyId = `${headingId}-body`;
  return (
    <section aria-labelledby={headingId} className="space-y-2">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={bodyId}
        className="flex w-full items-center gap-2 text-left rounded -m-1 p-1 hover:bg-muted/40 transition-colors"
      >
        {open ? (
          <ChevronDown className="h-4 w-4 text-muted-foreground" aria-hidden />
        ) : (
          <ChevronRight className="h-4 w-4 text-muted-foreground" aria-hidden />
        )}
        <h2 id={headingId} className="text-sm font-medium">
          {title}
        </h2>
        {count !== undefined && (
          <span className="text-xs text-muted-foreground tabular-nums">
            ({count})
          </span>
        )}
        {meta && (
          <span className="ml-auto text-xs text-muted-foreground">{meta}</span>
        )}
      </button>
      {open && <div id={bodyId}>{children}</div>}
    </section>
  );
}

function StatusBadge({ status }: { status: string }) {
  const cls = (() => {
    switch (status) {
      case 'running':
        return 'border-primary/30 bg-primary/15 text-foreground';
      case 'crashed':
        return 'border-destructive/30 bg-destructive/10 text-destructive';
      case 'stopped':
      default:
        return 'border-border bg-muted text-muted-foreground';
    }
  })();
  // Fixed width (w-24, 6rem) plus justify-center keeps running/stopped/
  // crashed pills visually identical despite the dot/icon/text differences.
  return (
    <span
      className={cn(
        'inline-flex items-center justify-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-medium w-24',
        cls,
      )}
    >
      {status === 'running' && (
        <span
          aria-hidden
          className="inline-block h-1.5 w-1.5 rounded-full bg-primary animate-pulse"
        />
      )}
      {status === 'crashed' && <AlertTriangle className="h-3 w-3" aria-hidden />}
      {status || 'unknown'}
    </span>
  );
}

function ResultBadge({ result }: { result: string }) {
  const failed = result === 'failure';
  return (
    <span
      className={cn(
        'inline-flex items-center justify-center rounded-full border px-2 py-0.5 text-[11px] font-medium w-20',
        failed
          ? 'border-destructive/30 bg-destructive/10 text-destructive'
          : 'border-border bg-muted text-muted-foreground',
      )}
    >
      {result || 'unknown'}
    </span>
  );
}

function RuntimeDisabledNotice() {
  return (
    <div className="text-sm text-muted-foreground">
      <Activity className="inline h-3.5 w-3.5 mr-1 -mt-0.5" aria-hidden />
      Runtime logging is disabled on this server. Recent sessions may not be
      recorded.
    </div>
  );
}

function OperationDisabledNotice() {
  return (
    <div className="text-sm text-muted-foreground">
      <Activity className="inline h-3.5 w-3.5 mr-1 -mt-0.5" aria-hidden />
      Operation logging is disabled on this server. Create/delete operations
      will not appear here while the flag is off.
    </div>
  );
}

// Renders a timestamp in the viewer's local timezone with a UTC tooltip.
// The wire mixes shapes: `startAt` and operation `timestamp` are RFC 3339
// (ISO with timezone), while `terminatedAt` is server-formatted as
// "YYYY-MM-DD HH:MM:SS UTC" or "-" for sessions still running / crashed.
// We accept either; values that don't parse fall through unchanged so the
// UI keeps showing whatever the server sent.
function formatLocal(s: string): string {
  if (!s || s === '-') return '—';
  let d = new Date(s);
  if (Number.isNaN(d.getTime())) {
    // "YYYY-MM-DD HH:MM:SS UTC" — turn into a strict ISO Z string before
    // retrying. Avoids implementation-defined Date parsing on Safari/older
    // engines.
    const m = s.match(/^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}:\d{2}) UTC$/);
    if (m) d = new Date(`${m[1]}T${m[2]}Z`);
  }
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}
